import {markDigitizationUI, ensurePvpStyle, pvViewportMargin} from "./DialogHelpers.js";

// Small floating popup for per-vertex/per-segment actions - "Slet punkt" on
// a hovered vertex (DigitizationModule._findHoveredVertex()), and the
// grouped "Vektoroperationer" menu on a hovered segment
// (DigitizationModule._findHoveredSegment(), see VectorEditingMenu.js) - both
// triggered by Shift+Right-click (vertex takes priority - see
// DigitizationModule's mouseup listener). A lightweight, cursor-anchored
// action list (not a blocking dialog), styled as PointView's `pv-menu`
// (frontend assets/pv-overlays.scss): light surface, 36 px rows, 6 px row
// radius, --pv-hover, group bands only when the menu really has several
// labelled groups. Shares the `pvm-*` skin in DialogHelpers.js (--pv-* tokens
// with standalone fallbacks).
//
// Viewport rule: the menu is capped at the viewport minus margin and scrolls
// internally, so no row can end up outside the viewport.
//
// One shared popup implementation (showGroupedContextMenu) for both -
// showVertexContextMenu() is a thin convenience wrapper over it (a single
// untitled group), kept so its existing call sites don't need to change.

let activeCleanup = null;

function closeActiveMenu(){
	if(!activeCleanup) return;
	activeCleanup();
	activeCleanup = null;
}

function createPanel(){
	ensurePvpStyle();
	const panel = document.createElement('div');
	panel.className = 'pvm-menu';
	markDigitizationUI(panel);
	return panel;
}

// `item`: {label, tooltip?, disabled?, onSelect} OR {label, tooltip?,
// submenu:[{label, tooltip?, onSelect}, ...]} - a submenu item opens a
// fly-out to the side on hover/click and has no onSelect of its own.
// `ctx`: {onDone, registerPanel, unregisterPanel, closeSiblingFlyouts} -
// shared plumbing so a fly-out is tracked for outside-click/Escape cleanup
// exactly like the root menu.
function buildItemRow(item, ctx){
	const row = document.createElement('div');
	const disabled = !!item.disabled;
	const hasSubmenu = Array.isArray(item.submenu) && item.submenu.length > 0;

	row.className = disabled ? 'pvm-item is-disabled' : 'pvm-item';
	const label = document.createElement('span');
	label.textContent = item.label;
	row.appendChild(label);
	if(item.tooltip) row.title = item.tooltip;
	if(hasSubmenu){
		const caret = document.createElement('span');
		caret.className = 'pvm-chevron';
		caret.textContent = '›';
		row.appendChild(caret);
	}

	if(disabled) return row;

	let flyout = null;
	let closeTimer = null;

	const closeFlyout = () => {
		if(closeTimer){ clearTimeout(closeTimer); closeTimer = null; }
		if(!flyout) return;
		ctx.unregisterPanel(flyout);
		ctx.untrackFlyoutCloser(closeFlyout);
		flyout.remove();
		flyout = null;
		row.classList.remove('is-open');
	};
	const scheduleClose = () => {
		if(closeTimer) clearTimeout(closeTimer);
		closeTimer = setTimeout(closeFlyout, 180);
	};
	const cancelClose = () => { if(closeTimer){ clearTimeout(closeTimer); closeTimer = null; } };

	const openFlyout = () => {
		if(flyout) return;
		ctx.closeSiblingFlyouts(closeFlyout);

		flyout = createPanel();
		for(const sub of item.submenu) flyout.appendChild(buildItemRow(sub, ctx));
		flyout.addEventListener('mouseenter', cancelClose);
		flyout.addEventListener('mouseleave', scheduleClose);
		document.body.appendChild(flyout);
		ctx.registerPanel(flyout);
		ctx.trackFlyoutCloser(closeFlyout);
		row.classList.add('is-open');

		// Placed next to the PARENT PANEL (not the row), so the fly-out never covers the menu it belongs to; flips to the
		// left when there is no room on the right, and is clamped to the viewport (max-height/scroll come from the CSS).
		const margin = pvViewportMargin();
		const parent = row.parentElement.getBoundingClientRect();
		const anchor = row.getBoundingClientRect();
		const fr = flyout.getBoundingClientRect();
		let left = parent.right + 2;
		if(left + fr.width > window.innerWidth - margin) left = parent.left - fr.width - 2;
		let top = anchor.top - 6;
		if(top + fr.height > window.innerHeight - margin) top = window.innerHeight - fr.height - margin;
		flyout.style.left = `${Math.max(margin, left)}px`;
		flyout.style.top = `${Math.max(margin, top)}px`;
	};

	row.addEventListener('mouseenter', () => { if(hasSubmenu) openFlyout(); });
	row.addEventListener('mouseleave', () => { if(hasSubmenu) scheduleClose(); });
	row.addEventListener('mouseup', (e) => {
		e.stopPropagation();
		if(hasSubmenu){
			openFlyout(); // click also opens it, for good measure
			return;
		}
		ctx.onDone();
		item.onSelect();
	});

	return row;
}

// actions: [{label, onSelect, disabled?}, ...] - single untitled group, the
// existing per-vertex "Slet punkt" shape.
export function showVertexContextMenu(clientX, clientY, actions){
	showGroupedContextMenu(clientX, clientY, {groups: [{items: actions}]});
}

// {title?, groups: [{label?, items: [{label, onSelect, disabled?}, ...]}, ...]}
// - `title` is an optional header (e.g. "Vektoroperationer"); each group may
// have its own `label` (e.g. "Geometri", "Vinkler") - the labels are only
// shown as section bands when the menu has two or more labelled groups (a
// single label would just be an artificial heading), otherwise groups are
// separated by a thin divider.
export function showGroupedContextMenu(clientX, clientY, {title = null, groups = []} = {}){
	closeActiveMenu();

	// Every open panel (the root menu + any live sub-menu fly-outs) - so an
	// outside-click / Escape can be told "inside the menu system" correctly,
	// and so cleanup removes all of them.
	const panels = [];
	const openFlyoutClosers = new Set();
	const ctx = {
		onDone: closeActiveMenu,
		registerPanel: (p) => panels.push(p),
		unregisterPanel: (p) => {
			const i = panels.indexOf(p);
			if(i !== -1) panels.splice(i, 1);
		},
		closeSiblingFlyouts: (keep) => {
			for(const close of [...openFlyoutClosers]) if(close !== keep) close();
		},
		trackFlyoutCloser: (close) => openFlyoutClosers.add(close),
		untrackFlyoutCloser: (close) => openFlyoutClosers.delete(close),
	};

	const menu = createPanel();
	panels.push(menu);

	if(title){
		const titleEl = document.createElement('div');
		titleEl.className = 'pvm-title';
		titleEl.textContent = title;
		menu.appendChild(titleEl);
	}

	const useBands = groups.filter((g) => g.label).length >= 2;
	groups.forEach((group, groupIndex) => {
		if(group.label && useBands){
			const labelEl = document.createElement('div');
			labelEl.className = 'pvm-band';
			labelEl.textContent = group.label;
			menu.appendChild(labelEl);
		}else if(groupIndex > 0){
			const divider = document.createElement('div');
			divider.className = 'pvm-sep';
			menu.appendChild(divider);
		}

		for(const item of group.items){
			menu.appendChild(buildItemRow(item, ctx));
		}
	});

	document.body.appendChild(menu);

	// A scrolled root menu moves its rows away from any open fly-out's anchor - close fly-outs instead of leaving them detached.
	menu.addEventListener('scroll', () => ctx.closeSiblingFlyouts(null));

	// clamp to viewport after layout (max-height/scroll are already capped by the CSS), so a menu opened near the
	// right/bottom edge doesn't spill off-screen
	const margin = pvViewportMargin();
	const rect = menu.getBoundingClientRect();
	const left = Math.min(clientX, window.innerWidth - rect.width - margin);
	const top = Math.min(clientY, window.innerHeight - rect.height - margin);
	menu.style.left = `${Math.max(margin, left)}px`;
	menu.style.top = `${Math.max(margin, top)}px`;

	const onOutsideMouseDown = (e) => {
		// "inside the menu system" = the root menu OR any open sub-menu fly-out
		if(!panels.some((p) => p.contains(e.target))) closeActiveMenu();
	};
	const onKeyDown = (e) => {
		if(e.key === "Escape") closeActiveMenu();
	};

	// deferred so the same Shift+RightClick mouseup that opened this menu
	// doesn't immediately close it via the outside-mousedown listener
	setTimeout(() => {
		document.addEventListener('mousedown', onOutsideMouseDown, true);
		document.addEventListener('keydown', onKeyDown);
	}, 0);

	activeCleanup = () => {
		document.removeEventListener('mousedown', onOutsideMouseDown, true);
		document.removeEventListener('keydown', onKeyDown);
		// remove every panel still open (root + any lingering fly-outs)
		for(const p of [...panels]) p.remove();
		panels.length = 0;
	};
}

export function closeVertexContextMenu(){
	closeActiveMenu();
}
